import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { AuthShell } from '../components/AuthShell';
import { login, type LoginRequest } from '../api/auth';
import { ApiError } from '../api/client';

/**
 * Login page.
 *
 * Replaces templates/login.html. Posts JSON to /api/auth/login;
 * server returns `{ redirect_to }` on success and the SPA does a
 * full-page nav to that URL (rather than client-side routing)
 * because the destination might be /admin/ - a different SPA - or
 * /dashboard, which (until PR-D) is still a Jinja template.
 *
 * OAuth buttons remain server-side redirects to /login/google and
 * /login/microsoft. Those callbacks haven't moved and won't move -
 * Google/Microsoft can't talk to a SPA, only to a Flask handler.
 */
/** Banner derived from URL query params. The signup + email-verify
 *  flows redirect here with one of these state markers:
 *    ?welcome=1&email=alice@acme.com  → just signed up; check inbox
 *    ?verified=1                      → email confirmed; sign in
 *    ?verify_failed=expired           → link timed out
 *    ?verify_failed=invalid           → link tampered / already used
 *  Returns `null` for any unrecognised combination so we don't
 *  accidentally surface noise from arbitrary query strings. */
type Banner =
  | { kind: 'welcome'; email: string }
  | { kind: 'verified' }
  | { kind: 'verify_failed'; reason: 'expired' | 'invalid' }
  | null;

function bannerFromParams(params: URLSearchParams): Banner {
  if (params.get('welcome') === '1') {
    return { kind: 'welcome', email: (params.get('email') || '').trim() };
  }
  if (params.get('verified') === '1') {
    return { kind: 'verified' };
  }
  const failReason = params.get('verify_failed');
  if (failReason === 'expired' || failReason === 'invalid') {
    return { kind: 'verify_failed', reason: failReason };
  }
  return null;
}

export function Login() {
  const [searchParams] = useSearchParams();
  const banner = useMemo(() => bannerFromParams(searchParams), [searchParams]);
  // Pre-fill email from `?email=` (welcome flow) AND from `?verified=1` if
  // a separate `email` param hitched a ride. Saves a typing step on the
  // most common post-signup path.
  const initialEmail = (searchParams.get('email') || '').trim().toLowerCase();
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (req: LoginRequest) => login(req),
    onSuccess: (res) => {
      // Full-page nav so we don't try to client-side-route into
      // /dashboard (still Jinja) or /admin/ (different SPA).
      window.location.assign(res.redirect_to);
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setErrorMessage(err.message);
      } else {
        setErrorMessage('Something went wrong. Please try again.');
      }
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrorMessage(null);
    mutation.mutate({ email: email.trim().toLowerCase(), password, remember });
  }

  return (
    <AuthShell
      title="Welcome back"
      subtitle="Sign in to your Locksmith account"
      footer={
        <>
          <p>
            Don&apos;t have an account? Contact your administrator for an
            invitation.
          </p>
          <p>
            <Link to="/reset-password">Forgot your password?</Link>
          </p>
        </>
      }
    >
      {banner && <SignupBanner banner={banner} />}

      {errorMessage && (
        <div className="auth-alert auth-alert-danger" role="alert">
          {errorMessage}
        </div>
      )}

      <form className="auth-form" onSubmit={handleSubmit} noValidate>
        <input
          className="auth-input"
          type="email"
          name="email"
          autoComplete="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={mutation.isPending}
          required
        />
        <input
          className="auth-input"
          type="password"
          name="password"
          autoComplete="current-password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={mutation.isPending}
          required
        />

        <div className="auth-checkbox-row">
          <input
            type="checkbox"
            id="remember"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
            disabled={mutation.isPending}
          />
          <label htmlFor="remember">Remember me</label>
        </div>

        <button
          type="submit"
          className="auth-btn"
          disabled={mutation.isPending || !email || !password}
        >
          {mutation.isPending ? 'Signing in…' : 'Sign in'}
        </button>
      </form>

      <div className="auth-divider">or continue with</div>

      <div className="auth-oauth-stack">
        {/* Server-side redirects - the Flask handlers haven't
            changed. Don't make these <Link>s; they need to leave
            the SPA so the OAuth round-trip can happen. */}
        <a className="auth-oauth-btn" href="/login/google">
          <GoogleIcon />
          Continue with Google
        </a>
        <a className="auth-oauth-btn" href="/login/microsoft">
          <MicrosoftIcon />
          Continue with Microsoft
        </a>
      </div>
    </AuthShell>
  );
}

/** Google "G" mark - official 4-colour logo. Inlined so we don't
 *  ship an extra HTTP request for a 1KB SVG. */
function GoogleIcon() {
  return (
    <svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

/** Microsoft 4-square mark. */
function MicrosoftIcon() {
  return (
    <svg viewBox="0 0 23 23" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect x="1" y="1" width="10" height="10" fill="#F25022" />
      <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
      <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
      <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
    </svg>
  );
}

/** Banner shown above the form for the signup / verification flows.
 *  All four states reuse the .auth-alert-* palette so this stays
 *  visually consistent with the form's own error rendering. */
function SignupBanner({ banner }: { banner: NonNullable<Banner> }) {
  if (banner.kind === 'welcome') {
    return (
      <div className="auth-alert auth-alert-success" role="status">
        <strong>Workspace created.</strong>{' '}
        {banner.email
          ? <>We sent a verification link to <strong>{banner.email}</strong>. Click it to activate your account, then sign in here.</>
          : <>Check your inbox for a verification link, then sign in here.</>}
      </div>
    );
  }
  if (banner.kind === 'verified') {
    return (
      <div className="auth-alert auth-alert-success" role="status">
        <strong>Email confirmed.</strong> Sign in to continue.
      </div>
    );
  }
  // verify_failed
  return (
    <div className="auth-alert auth-alert-warning" role="alert">
      {banner.reason === 'expired' ? (
        <>
          <strong>That verification link has expired.</strong> Verification
          links last one hour. Sign up again to get a fresh one, or
          contact support if you've already created an account.
        </>
      ) : (
        <>
          <strong>That verification link is no longer valid.</strong> It may
          have been used already, or the URL was mistyped. Try signing
          in below; if your email isn't verified yet, contact support.
        </>
      )}
    </div>
  );
}
